import { find_meta_variant } from "@core/helpers/server/product-meta";

/**
 * ساختِ Product / ProductGroup JSON-LD برای صفحه‌ی تک‌محصول.
 *
 * چرا اینجا و نه در هسته؟ تابعِ `get_product_JsonLd_server` در
 * `@core/helpers/server/getJsonLds` چند ایراد دارد ولی چند سایتِ دیگر هم از همان
 * استفاده می‌کنند؛ طبق قانون additive-only هسته دست‌نخورده می‌ماند و نسخه‌ی درستِ
 * ژومنت اینجا نگه‌داری می‌شود. ایرادهای نسخه‌ی هسته:
 *
 * 1. `url` را با `${origin}/${seo.full_url || seo.url}` می‌سازد. در این بک‌اند
 *    `seo.url` فقط اسلاگ است، پس آدرس به یک صفحه‌ی ۴۰۴ اشاره می‌کرد؛ و اگر
 *    `full_url` هم می‌آمد (که با `/` شروع می‌شود) نتیجه `origin//product/...` بود.
 * 2. رنگِ هر واریانت را از `labels_obj.color` می‌خواند ولی کلیدِ واقعیِ بک‌اند
 *    `labels_obj.colors` است - یعنی هیچ واریانتی `color` نمی‌گرفت، در حالی که
 *    `variesBy` رنگ را اعلام می‌کرد.
 * 3. `hasVariant` / `variesBy` را روی `@type: "Product"` می‌گذارد. این دو مخصوصِ
 *    `ProductGroup` هستند و چون در حالتِ واریانت‌دار هیچ `offers`ی هم ساخته
 *    نمی‌شود، گوگل کل آیتم را با خطای «Missing field offers» رد می‌کند.
 * 4. `variesBy` همیشه فقط رنگ است، حتی وقتی واریانت‌ها بر اساس سایز فرق دارند.
 */

/**
 * نامِ فروشنده در `offers.seller`. تنظیماتِ سایت فیلدِ «نام سایت» ندارد
 * (`cache-data/siteSetting` فقط لوگو، ارز، مسیرها و … را می‌دهد) و مقدارِ
 * `og:site_name` از سئوی هر صفحه می‌آید، پس مثل `SingleBlog/index.jsx` اینجا
 * هم ثابت نوشته می‌شود.
 */
export const SELLER_NAME = "ژومنت استایل";

const VARIANT_DIMENSIONS = [
	{ label_key: "colors", property: "color", schema_url: "https://schema.org/color" },
	{ label_key: "sizes", property: "size", schema_url: "https://schema.org/size" },
];

const clean_text = (value) => {
	if (typeof value !== "string") return undefined;
	const text = value
		.replace(/<[^>]*>/g, " ")
		.replace(/&nbsp;/g, " ")
		.replace(/\s+/g, " ")
		.trim();
	return text.length > 0 ? text : undefined;
};

/** شناسه‌ها (sku / product_code) گاهی نیم‌فاصله یا فاصله‌ی اضافه دارند. */
const clean_identifier = (value) => {
	if (typeof value !== "string") return undefined;
	const text = value.replace(/[​-‏﻿]/g, "").trim();
	return text.length > 0 ? text : undefined;
};

/**
 * تکه‌ی انگلیسیِ قابل استفاده در فرگمنتِ `@id`. کلیدهای لیبل در پنل انگلیسی‌اند
 * (`red`، `dark-navy`، `one`)، ولی اگر کلیدی فارسی یا خالی بود `undefined`
 * برمی‌گردد تا به شماره‌ی واریانت برگردیم.
 */
const slug_part = (value) => {
	if (typeof value !== "string") return undefined;
	const slug = value
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");
	return slug.length > 0 ? slug : undefined;
};

/** `trailingSlash: true` است، پس همه‌ی آدرس‌ها باید با `/` تمام شوند. */
export const absolute_page_url = (origin = "", path = "") => {
	if (!path) return undefined;
	if (/^https?:\/\//i.test(path)) return path;
	const with_leading = path.startsWith("/") ? path : `/${path}`;
	const with_trailing = with_leading.endsWith("/") ? with_leading : `${with_leading}/`;
	return `${(origin || "").replace(/\/+$/, "")}${with_trailing}`;
};

const media_url = (prefix = "", media) => {
	const url = media?.url;
	if (!url) return undefined;
	if (/^https?:\/\//i.test(url)) return url;
	return `${(prefix || "").replace(/\/+$/, "")}/${url.replace(/^\/+/, "")}`;
};

const gallery_urls = (prefix, gallery) => {
	const list = (gallery || []).map((item) => media_url(prefix, item)).filter(Boolean);
	return list.length > 0 ? list : undefined;
};

/** قیمتِ قابل پرداخت. در این بک‌اند `main_price` قیمت نهایی است و `price` قیمتِ خط‌خورده. */
const payable_price = (price) => {
	const value = Number(price?.main_price);
	return Number.isFinite(value) && value > 0 ? value : undefined;
};

const availability_url = (count, price) =>
	`https://schema.org/${Number(count) > 0 && payable_price(price) ? "InStock" : "OutOfStock"}`;

const build_seller = (seller_name) =>
	seller_name ? { "@type": "Organization", name: seller_name } : undefined;

/*
 * بک‌اند برای واریانتِ ناموجود `main_price: 0` برمی‌گرداند، پس بدونِ این
 * fallback آن واریانت یک `Offer` بی‌قیمت می‌شد - چیزی که گوگل نامعتبر می‌داند.
 * `fallback_price` قیمتِ ریشه‌ی محصول است؛ همان عددی که فروشنده می‌فروشد.
 */
const build_offer = ({ price, details, url, currency, seller_name, fallback_price }) => {
	const value = payable_price(price) ?? fallback_price;
	const offer = {
		"@type": "Offer",
		priceCurrency: currency,
		availability: availability_url(details?.count, price),
		itemCondition: "https://schema.org/NewCondition",
	};
	if (value !== undefined) offer.price = value;
	if (url) offer.url = url;
	const seller = build_seller(seller_name);
	if (seller) offer.seller = seller;
	return offer;
};

/** فقط بُعدهایی که واقعاً بین واریانت‌ها فرق دارند وارد `variesBy` می‌شوند. */
const varying_dimensions = (mix_variant = []) =>
	VARIANT_DIMENSIONS.filter(({ label_key }) => {
		const titles = new Set(
			mix_variant
				.map((item) => clean_text(item?.labels_obj?.[label_key]?.title))
				.filter(Boolean),
		);
		return titles.size > 1;
	});

const variant_name = (product_title, item, dimensions) => {
	const parts = dimensions
		.map(({ label_key }) => clean_text(item?.labels_obj?.[label_key]?.title))
		.filter(Boolean);
	if (parts.length === 0) {
		return [product_title, clean_text(item?.title)].filter(Boolean).join(" ");
	}
	return [product_title, ...parts].filter(Boolean).join(" - ");
};

const build_aggregate_rating = (product) => {
	const count = Number(product?.rate_counts?.total);
	const value = Number(product?.average_rate);
	// گوگل روی ratingValue بدونِ رأی خطا می‌دهد، پس بدونِ رأی اصلاً ننویس.
	if (!Number.isFinite(count) || count < 1) return undefined;
	if (!Number.isFinite(value) || value <= 0) return undefined;
	return {
		"@type": "AggregateRating",
		ratingValue: value,
		reviewCount: count,
		bestRating: 5,
		worstRating: 1,
	};
};

const build_aggregate_offer = ({ offers, currency, url, seller_name }) => {
	const prices = offers.map((offer) => offer?.price).filter((price) => Number.isFinite(price));
	if (prices.length === 0) return undefined;
	const in_stock = offers.some((offer) => offer?.availability?.endsWith("InStock"));
	const aggregate = {
		"@type": "AggregateOffer",
		priceCurrency: currency,
		lowPrice: Math.min(...prices),
		highPrice: Math.max(...prices),
		offerCount: offers.length,
		availability: `https://schema.org/${in_stock ? "InStock" : "OutOfStock"}`,
	};
	if (url) aggregate.url = url;
	const seller = build_seller(seller_name);
	if (seller) aggregate.seller = seller;
	return aggregate;
};

const drop_empty = (obj) =>
	Object.fromEntries(
		Object.entries(obj).filter(([, value]) => {
			if (value === undefined || value === null) return false;
			if (Array.isArray(value)) return value.length > 0;
			return true;
		}),
	);

/**
 * @param {object} args
 * @param {object} args.product            پاسخِ `getProductBySlug`
 * @param {string} args.origin             مثلاً `https://jument.style`
 * @param {string} args.storage_prefix_url پیشوندِ CDN تصاویر
 * @param {string} [args.summary]          HTML ویجتِ «معرفی محصول»
 * @param {string} [args.description]      HTML ویجتِ «بررسی تخصصی»
 * @param {string} [args.currency]         پیش‌فرض IRR؛ قیمت‌های بک‌اند ریالی‌اند
 * @param {string} [args.seller_name]      نامِ فروشنده در `offers.seller`
 * @param {string} [args.selected_labels]  مقدارِ `?selected-labels=` صفحه
 *
 * @returns {{group: object|undefined, product: object|undefined, selected: object|undefined}}
 *   سه اسکیمای جدا، هر کدام در تگ `<script>` خودش:
 *   - `group`    `ProductGroup` با همه‌ی واریانت‌ها در `hasVariant`.
 *   - `product`  خودِ محصول با داده‌ی ریشه (قیمت، موجودی و کدِ ریشه).
 *   - `selected` واریانتِ نمایش‌داده‌شده به‌صورت یک `Product` مستقل.
 *   محصولِ بدونِ واریانت فقط `product` دارد.
 */
export const build_product_json_lds = ({
	product,
	origin = "",
	storage_prefix_url = "",
	summary,
	description,
	currency = "IRR",
	seller_name = SELLER_NAME,
	selected_labels,
} = {}) => {
	if (!product) return { group: undefined, product: undefined, selected: undefined };

	const url = absolute_page_url(origin, product?.seo?.full_url || product?.seo?.url);
	const images = gallery_urls(storage_prefix_url, product?.media_gallery);
	const title = clean_text(product?.title);
	const mix_variant = Array.isArray(product?.mix_variant) ? product.mix_variant : [];

	/*
	 * متنِ محصول از دو ویجت می‌آید، نه از رکورد محصول: `product.description` و
	 * `product.summary` بازنشسته شده‌اند (پنل دیگر پرشان نمی‌کند و روی کل
	 * محصولات `null` برمی‌گردند).
	 *
	 * «بررسی تخصصی» متنِ اصلی است و به `description` می‌رود؛ «معرفی محصول»
	 * خلاصه است و به `disambiguatingDescription` (تنها جای استانداردِ خلاصه روی
	 * Product) می‌رود. اگر بررسی تخصصی نبود، معرفی محصول خودش `description`
	 * می‌شود تا این فیلد خالی نماند - و در آن حالت خلاصه نوشته نمی‌شود تا یک
	 * متن دوبار تکرار نشود.
	 */
	const intro_text = clean_text(summary);
	const review_text = clean_text(description);

	const base = drop_empty({
		"@context": "https://schema.org",
		name: title,
		description: review_text || intro_text,
		disambiguatingDescription: review_text ? intro_text : undefined,
		image: images,
		url,
		category: clean_text(product?.category?.title),
		brand: product?.brand?.title
			? { "@type": "Brand", name: clean_text(product?.brand?.title) }
			: undefined,
		aggregateRating: build_aggregate_rating(product),
		// اینجا `inLanguage` نگذار: مالِ CreativeWork است و schema.org رویِ
		// Product/ProductGroup آن را نمی‌شناسد (تنها وارنینگِ ولیدیتور بود).
	});

	/* قیمتِ جایگزین برای واریانت‌های ناموجود (که بک‌اند صفر می‌دهد): اول قیمتِ
	   ریشه‌ی محصول، وگرنه اولین واریانتی که قیمت دارد. */
	const fallback_price =
		payable_price(product?.price) ??
		mix_variant.map((item) => payable_price(item?.price)).find((value) => value !== undefined);

	/*
	 * خودِ محصول، مستقل از واریانت‌ها: قیمت، موجودی و کد از ریشه‌ی محصول
	 * می‌آیند. `@id` چه محصول واریانت داشته باشد چه نه `#product` است، پس
	 * شناسه‌ی این موجودیت با اضافه یا حذف شدنِ واریانت عوض نمی‌شود.
	 */
	const product_node = drop_empty({
		...base,
		"@type": "Product",
		"@id": url ? `${url}#product` : undefined,
		sku: clean_identifier(product?.details?.product_code) || product?.id,
		offers: build_offer({
			price: product?.price,
			details: product?.details,
			url,
			currency,
			seller_name,
			fallback_price,
		}),
	});

	if (mix_variant.length === 0) {
		// بدونِ واریانت نه گروهی هست و نه چیزی برای «انتخاب شدن»؛ فقط خودِ محصول.
		return { group: undefined, product: product_node, selected: undefined };
	}

	const dimensions = varying_dimensions(mix_variant);

	/* فرگمنتِ `@id` از کلیدِ لیبل‌ها ساخته می‌شود (`#red-one`) تا خوانا و پایدار
	   باشد؛ اگر کلیدی نبود یا دو واریانت به یک اسلاگ رسیدند، به شماره برمی‌گردیم
	   چون یکتا بودنِ `@id` شرط است. */
	const used_slugs = new Set();
	const variant_fragment = (item, index) => {
		const slug = dimensions
			.map(({ label_key }) => slug_part(item?.labels_obj?.[label_key]?.key))
			.filter(Boolean)
			.join("-");
		if (!slug || used_slugs.has(slug)) return `variant-${index + 1}`;
		used_slugs.add(slug);
		return slug;
	};

	const variants = mix_variant.map((item, index) => {
		const variant = drop_empty({
			"@type": "Product",
			"@id": url ? `${url}#${variant_fragment(item, index)}` : undefined,
			name: variant_name(title, item, dimensions),
			sku: clean_identifier(item?.details?.product_code),
			image: gallery_urls(storage_prefix_url, item?.media_gallery) || images,
			offers: build_offer({
				price: item?.price,
				details: item?.details,
				url,
				currency,
				seller_name,
				fallback_price,
			}),
		});
		for (const { label_key, property } of dimensions) {
			const value = clean_text(item?.labels_obj?.[label_key]?.title);
			if (value) variant[property] = value;
		}
		return variant;
	});

	const group_id = url ? `${url}#product-group` : undefined;

	const group = drop_empty({
		...base,
		"@type": "ProductGroup",
		"@id": group_id,
		// عمداً `id` و نه `product_code`: بک‌اند یک `product_code` مشترک به همه‌ی
		// واریانت‌ها می‌دهد و productGroupID باید از sku واریانت‌ها جدا باشد.
		productGroupID: product?.id || clean_identifier(product?.details?.product_code),
		variesBy: dimensions.map(({ schema_url }) => schema_url),
		hasVariant: variants,
		offers: build_aggregate_offer({
			offers: variants.map((variant) => variant?.offers),
			currency,
			url,
			seller_name,
		}),
	});

	/*
	 * واریانتِ نمایش‌داده‌شده به‌صورت یک `Product` مستقل و کامل.
	 *
	 * گرهِ داخلِ `hasVariant` عمداً لاغر است (فقط sku/رنگ/سایز/قیمت). این یکی
	 * فیلدهای سطحِ محصول - توضیحات، برند، دسته، امتیاز - را هم می‌گیرد تا به‌تنهایی
	 * یک محصولِ کامل باشد.
	 *
	 * این اسکیما باید یک آیتمِ مستقل بماند، پس نباید در گرافِ JSON-LD به گروه
	 * بچسبد:
	 * - `@id` آن با گرهِ داخلِ `hasVariant` فرق دارد (`#large-selected` نه
	 *   `#large`). با `@id` یکسان، ولیدیتور دو گره را یک موجودیت می‌داند و این
	 *   اسکیما را داخلِ `hasVariant` ادغام می‌کند.
	 * - اتصال به گروه با `inProductGroupWithID` (متنی، برابرِ `productGroupID`)
	 *   است، نه `isVariantOf: {"@id"}`؛ ارجاعِ `@id` هم گره‌ها را تو در تو می‌کند.
	 */
	// the variant the page shows on load: core's choice for the `product:*` meta tags and useProduct (the one carrying
	// every `?selected-labels=` key, else first available, else is_main_price, else the first)
	const selected_item = find_meta_variant(mix_variant, selected_labels);
	const selected_node = variants[mix_variant.indexOf(selected_item)];

	const selected = selected_node
		? drop_empty({
				...base,
				...selected_node,
				"@id": selected_node["@id"] ? `${selected_node["@id"]}-selected` : undefined,
				inProductGroupWithID: group.productGroupID,
			})
		: undefined;

	return { group, product: product_node, selected };
};

/**
 * خروجیِ امنِ `<script type="application/ld+json">`.
 * بدونِ escape کردنِ `<`، یک `</script>` داخلِ توضیحاتِ محصول تگ را می‌بندد.
 */
export const json_ld_script_content = (json_ld) =>
	JSON.stringify(json_ld ?? {})
		.replace(/</g, "\\u003c")
		.replace(/>/g, "\\u003e")
		.replace(/&/g, "\\u0026");
